import {CreateFunctionRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/CreateFunctionRequest';
import {FunctionResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/FunctionResponse';
import {ValidationErrorDescription} from '@mintdata-wsp/common/controllers/network/generated/responses/ValidationErrorDescription';
import {FlowExecType} from '@mintdata-wsp/common/controllers/flow/FlowController';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {FlowStateUtils} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowStateUtils';
import {IFocusActivator} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FocusActivator';
import {Slot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';
import {SurfacePoint} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {
    CloudComponent,
    CloudComponentId,
    CloudPort,
    CloudProperty,
    CloudTick,
    CloudTickUnits
} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {FlowUtil} from '@mintdata-wsp/common/util/FlowUtil';
import {MobxRequestable} from '@mintdata-wsp/common/util/MobxRequestable';
import {FunctionSpec, IFunctionArgSpec, IFunctionSpec} from '@mintdata/mint-kit/api/decorators/Function';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {BoxId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import {computed, observable} from 'mobx';

export const INPUT_COMPONENT_NAME = 'InputComponent';
export const OUTPUT_COMPONENT_NAME = 'OutputComponent';
export const ENTRY_COMPONENT_NAME = 'FrameFromInternalJsonComponent';

export class FlowState
{
    public topBar:FlowTopBarState = new FlowTopBarState();
    public glass:FlowGlassState = new FlowGlassState();
    public canvas:FlowCanvasState = new FlowCanvasState();

    @observable
    public flows:Flow[] = [];

    @observable
    public cloudFunctions:CloudFunction[] = null;

    @observable
    public openFlow:Flow = null;

    @observable
    public isNewFlowCreating:boolean = false;

    @observable
    public flowSimulations:FlowSimulation[] = [];
}

export class CloudFunction
{
    public id:number;

    public outputComponentId:string;

    public spec:FunctionSpec;

    constructor(id:number,
                outputComponentId:string,
                spec:FunctionSpec)
    {
        this.id = id;
        this.outputComponentId = outputComponentId;
        this.spec = spec;
    }
}

export class Flow
{
    public id:number;
    public projectId:number;
    public designId:number;

    @observable
    public name:string;

    @observable
    public flowBlocks:FlowBlock[];

    @observable
    public flowErrors:FlowValidationError[] = [];

    @observable
    public cloudFlowErrors:ValidationErrorDescription[] = [];

    @observable
    public lastInputValues:Map<string, string> = new Map();

    @observable
    public renameErrorMessage:string = null;

    constructor(id:number,
                projectId:number,
                designId:number,
                name:string,
                flowBlocks:FlowBlock[] = [])
    {
        this.id = id;
        this.projectId = projectId;
        this.designId = designId;
        this.name = name;
        this.flowBlocks = flowBlocks;
    }

    @computed
    public get inputs():FlowBlock[]
    {
        return this.flowBlocks.filter(({name}) => name === INPUT_COMPONENT_NAME);
    }

    public get orderedInputNames():string[]
    {
        return this.inputs
            .sort((a, b) =>
            {
                const getOrder = (flowBlock:FlowBlock):number =>
                {
                    const property = flowBlock.properties.find(({name}) => name === 'order');
                    return property ? property.value : 1;
                };

                return getOrder(a) - getOrder(b);
            })
            .map(({instanceName}) => instanceName);
    }

    @computed
    public get output():FlowBlock
    {
        return this.flowBlocks.find(({name}) => name === OUTPUT_COMPONENT_NAME);
    }

    @computed
    public get flowEntryBlock():FlowBlock
    {
        return this.flowBlocks.find(({isEntryBlock}) => isEntryBlock);
    }

    @computed
    public get topology():TopologyState
    {
        return FlowStateUtils.generateTopologyState(this.flowBlocks);
    }

    @computed
    public get spec():FunctionSpec
    {
        const spec:IFunctionSpec = FlowStateUtils.generateFuncSpec(this);

        const funcArgs:IFunctionArgSpec[] = FlowStateUtils.generateFuncArgSpecs(this.inputs);
        const funcSpec = new FunctionSpec();

        funcSpec.setValuesFrom(spec);
        funcSpec.args = funcArgs;

        return funcSpec;
    }

    @computed
    public get cloudFunction():CloudFunction
    {
        return new CloudFunction(this.id, this.output.id, this.spec);
    }

    public static fromResponse(response:FunctionResponse):Flow
    {
        const {id, name, body} = response;
        const {visualSettings} = body;

        const jsonVisualSettings = JSON.parse(visualSettings);
        const jsonFlowBlocks = jsonVisualSettings.flowBlocks;

        const flowBlocks = jsonFlowBlocks.map((blockJson:FlowBlock) => FlowBlock.fromJson(blockJson));

        const ctx = new FlowSerdesContext();

        flowBlocks.map((b:FlowBlock) => ctx.putBlock(b));

        jsonFlowBlocks.map((blockJson:FlowBlock) =>
        {
            const dehydratedBlock = ctx.findBlock(blockJson.id);

            const inWires = blockJson.inWires.map((w:FlowWire) => FlowWire.fromJson(w, ctx));
            const outWires = blockJson.outWires.map((w:FlowWire) => FlowWire.fromJson(w, ctx));

            dehydratedBlock.inWires = inWires;
            dehydratedBlock.outWires = outWires;
        });

        return new Flow(id, null, null, name, flowBlocks);
    }

    public toCreateRequest():CreateFunctionRequest
    {
        const entryBlockPropertyName = 'value';
        const outputBlock = this.flowBlocks.find(({name}) => name === OUTPUT_COMPONENT_NAME);

        return {
            artifacts: FlowStateUtils.getArtifactsForSaveRequest(),
            name: this.name,
            description: null,
            example: null,
            input: {
                id: this.flowEntryBlock.id,
                propertyName: entryBlockPropertyName
            },
            output: {
                id: outputBlock.id
            },
            body: {
                ...this.topology,
                visualSettings: JSON.stringify({
                    flowBlocks: this.flowBlocks
                }),
                settings: null
            },
            designVersion: rootState.ui.visualDesigner.openDesign.designRef.version
        };
    }

    public toUpdateRequest():CreateFunctionRequest & {id:number}
    {
        return {
            ...this.toCreateRequest(),
            id: this.id
        };
    }
}

export class TopologyState
{
    components:any[] = [];
    links:any[] = [];
}

export class FlowTopBarState
{
    @observable
    public isWonderBarVisible:boolean = false;

    @observable
    public wonderBarDomRef:HTMLElement | null = null;
}

export class FlowCanvasState
{
    public readonly id:GUID = IdUtil.newGUID();

    @observable
    public canvasDomElement:HTMLElement = null;

    @observable
    public canvasFocusActivator:IFocusActivator = null;

    @observable
    public wiresSvgElement:SVGElement = null;

    @observable
    public selectedBlocks:FlowBlock[] = [];

    public flashBlock:MobxRequestable<FlashBlockRequest> = new MobxRequestable();

    // An 'active' wire is a new wire that's being dragged from
    // some port (input or output port). As a result, it always
    // has the value of 'null' for one of its anchors.
    @observable
    public activeWire:FlowWire = null;

    // A selected wire is always one of the 'permanent wires'.
    // A permanent wire is one that is already connected to both ports
    // and is thus a part of teh design state. Once a permanent
    // wire is selected, it can be then deleted by the user and/or
    // manipulated.
    @observable
    public selectedWire:FlowWire = null;

    @observable
    public mouseOverPort:FlowPortContext = null;

    // Note: these fields do not need to be observable:
    public mouseOverBlock:FlowBlockContext | null = null;

    @observable
    public wireContextMenu:FlowWireMenuContext = null;

    @observable
    public blockContextMenu:FlowBlockMenuContext = null;
}

export class FlowGlassState
{
    @observable
    public dragContext:FlowDragContext = null;
}

export class FlowDragContext
{
    constructor(public mouseDownOnElement:HTMLElement,
                public elementBoundingClientRect:ClientRect,
                public mouseEvent:MouseEvent,
                public componentToDrag:FlowComponent)
    {
    }
}

export class FlowComponent
{
    @observable
    public isVisible:boolean = true;

    constructor(public readonly section:FlowSection,
                public readonly component:CloudComponent)
    {
    }
}

export type FlowSectionId = GUID;

export class FlowSection
{
    public readonly id:FlowSectionId = IdUtil.newGUID();

    @observable
    public isExpanded:boolean = true;

    @observable
    public components:FlowComponent[] = [];

    constructor(public readonly sectionName:string,
                public readonly artifactName:string)
    {
    }

    public get hasVisibleComponents():boolean
    {
        let hasVisible = false;

        for( let c of this.components )
        {
            if( c.isVisible )
                hasVisible = true;
        }

        return hasVisible;
    }
}

export type FlowBlockId = GUID;

export class FlowBlock
{
    @observable
    public instanceName:string = null;

    @observable
    public outWires:FlowWire[] = [];

    @observable
    public inWires:FlowWire[] = [];

    constructor(public readonly id:FlowBlockId,
                public readonly bounds:FlowBounds,
                public readonly isDebugBlock:boolean,
                public readonly isEntryBlock:boolean,
                public readonly visualLink:VisualLink | null,
                public readonly component:CloudComponent,
                public readonly properties:FlowProperty[],
                public readonly ticks:FlowTick[],
                public readonly inputPorts:FlowPort[],
                public readonly outputPorts:FlowPort[])
    {
    }

    public get isVisualLink():boolean
    {
        return !!this.visualLink;
    }

    public get name():string
    {
        return this.component.name;
    }

    public get description():string
    {
        return this.component.description;
    }

    public isEqualTo(other:FlowBlock):boolean
    {
        if( !other )
            return false;

        return other.id === this.id;
    }

    public clone():FlowBlock
    {
        const {
            id, bounds, isDebugBlock, isEntryBlock, visualLink,
            component, properties, ticks, inputPorts, outputPorts,
            instanceName, inWires, outWires
        } = this;

        const result = new FlowBlock(
            id,
            bounds.clone(),
            isDebugBlock,
            isEntryBlock,
            visualLink && visualLink.clone(),
            component,
            properties.map(prop => prop.clone()),
            ticks.map(tick => tick.clone()),
            inputPorts.map(port => port.clone()),
            outputPorts.map(port => port.clone())
        );

        result.instanceName = instanceName;

        return result;
    }

    public static fromJson(json:FlowBlock):FlowBlock
    {
        const component = CloudComponent.fromJson(json.component);
        const properties = json.properties.map((p:FlowProperty) =>
        {
            return FlowProperty.fromJson(p);
        });

        const ticks = json.ticks.map((p:FlowTick) =>
        {
            return FlowTick.fromJson(p);
        });

        const inPorts = json.inputPorts.map((p:FlowPort) =>
        {
            return FlowPort.fromJson(p);
        });

        const outPorts = json.outputPorts.map((p:FlowPort) =>
        {
            return FlowPort.fromJson(p);
        });

        const bounds = FlowBounds.fromJson(json.bounds);
        const isDebugBlock = !!json.isDebugBlock;
        const isEntryBlock = !!json.isEntryBlock;
        const visualLink = json.visualLink ? VisualLink.fromJson(json.visualLink) : null;

        const block = new FlowBlock(
            json.id,
            bounds,
            isDebugBlock,
            isEntryBlock,
            visualLink,
            component,
            properties,
            ticks,
            inPorts,
            outPorts
        );

        block.instanceName = json.instanceName;
        return block;
    }

    toString()
    {
        return `[Block id:${this.id} name:${this.component.name} instanceName: ${this.instanceName}]`;
    }
}

export class FlowProperty
{
    @observable
    public value:any = null;

    @observable
    public meta:any = null;

    constructor(public readonly definition:CloudProperty)
    {
        this.value = definition.defaultValue;
    }

    public get name():string
    {
        return this.definition.name;
    }

    public isEqualTo(other:FlowProperty):boolean
    {
        return other && other.definition && other.definition.name === this.name;
    }

    public clone():FlowProperty
    {
        const {definition, value, meta} = this;

        const result = new FlowProperty(definition);
        result.meta = meta;
        result.value = value;

        return result;
    }

    public static fromJson(json:FlowProperty):FlowProperty
    {
        const definition = CloudProperty.fromJson(json.definition);
        const flowProperty = new FlowProperty(definition);

        flowProperty.value = json.value;
        flowProperty.meta = json.meta;

        return flowProperty;
    }
}

export class FlowTick
{
    @observable
    public interval:number = null;

    @observable
    public unit:CloudTickUnits = null;

    constructor(public readonly definition:CloudTick)
    {
    }

    public clone():FlowTick
    {
        const {definition, interval, unit} = this;

        const result = new FlowTick(definition);
        result.interval = interval;
        result.unit = unit;

        return result;
    }

    public static fromJson(json:FlowTick):FlowTick
    {
        const definition = CloudTick.fromJson(json.definition);
        const flowTick = new FlowTick(definition);

        flowTick.interval = json.interval;
        flowTick.unit = json.unit;

        return flowTick;
    }
}

export enum FlowPortKind
{
    InputPort = 'InputPort',
    OutputPort = 'OutputPort'
}

export class FlowPort
{
    constructor(public readonly id:GUID,
                public readonly kind:FlowPortKind,
                public readonly definition:CloudPort)
    {
    }

    public isEqualTo(other:FlowPort):boolean
    {
        if( !other )
            return false;

        return other.id === this.id;
    }

    public get name():string
    {
        return this.definition.name;
    }

    public isRequired():boolean
    {
        return !this.definition.isOptional;
    }

    public clone():FlowPort
    {
        return new FlowPort(this.id, this.kind, this.definition);
    }

    public static fromJson(json:FlowPort):FlowPort
    {
        return new FlowPort(json.id, json.kind, json.definition);
    }
}

export class FlowAnchor implements IFlowAnchor
{
    constructor(public readonly block:FlowBlock | null,
                public readonly port:FlowPort | null)
    {
    }

    @computed
    public get position():FlowPoint
    {
        return FlowUtil.getAbsolutePortPosition(this.block, this.port);
    }

    public isEqualTo(other:FlowAnchor):boolean
    {
        if( !other )
            return false;

        if( other.port && other.block )
            return other.port.isEqualTo(this.port) &&
                other.block.isEqualTo(this.block) &&
                other.position.isEqual(this.position);
        else if( other.position )
            return other.position.isEqual(this.position);
        else
            return false;
    }

    @bind
    public toJSON():any
    {
        return {
            blockId: this.block.id,
            port: this.port
        };
    }

    public clone(cloneCtx:FlowSerdesContext):FlowAnchor
    {
        const block = cloneCtx.findBlock(this.block.id);
        const port = this.port.clone();
        return new FlowAnchor(block, port);
    }

    public static fromJson(json:IFlowAnchor, ctx:FlowSerdesContext):FlowAnchor
    {
        const block = ctx.findBlock((json as any).blockId);
        const port = FlowPort.fromJson(json.port);

        return new FlowAnchor(block, port);
    }
}

export class FlowWire
{
    constructor(public readonly source:IFlowAnchor,
                public readonly target:IFlowAnchor)
    {
    }

    public get id():string
    {
        if( this.target.port && this.source.port )
            return `wire-${this.source.port.id}-${this.target.port.id}`;
        else if( this.source.port )
            return `active-from-source-wire-${this.source.port.id}`;
        else if( this.target.port )
            return `active-from-target-wire-${this.target.port.id}`;
        else
            throw new Error(`Error: invalid wire: contains no source and no target: ${this}`);
    }

    public clone(cloneCtx:FlowSerdesContext):FlowWire
    {
        return new FlowWire(
            this.source.clone(cloneCtx),
            this.target.clone(cloneCtx)
        );
    }

    public isEqualTo(other:FlowWire)
    {
        if( !other )
            return false;

        return other && other.source && other.target &&
            other.source.isEqualTo(this.source) &&
            other.target.isEqualTo(this.target);
    }

    public isConnected():boolean
    {
        return !!this.source &&
            !!this.target &&
            !!this.source.port &&
            !!this.target.port &&
            !!this.source.block &&
            !!this.target.block;
    }

    public static fromJson(json:FlowWire, ctx:FlowSerdesContext):FlowWire
    {
        const source = FlowAnchor.fromJson(json.source, ctx);
        const target = FlowAnchor.fromJson(json.target, ctx);

        return new FlowWire(source, target);
    }
}

export interface IFlowAnchor
{
    block:FlowBlock;
    port:FlowPort;
    position:FlowPoint;

    isEqualTo(other:IFlowAnchor):boolean;
    clone(cloneCtx:FlowSerdesContext):IFlowAnchor;
}

export class ActiveWireFlowAnchor implements IFlowAnchor
{
    private readonly _mousePosition:FlowPoint;

    constructor(mousePosition:FlowPoint)
    {
        this._mousePosition = mousePosition;
    }

    get position():FlowPoint
    {
        return this._mousePosition;
    }

    get block():FlowBlock
    {
        return null;
    }

    get port():FlowPort
    {
        return null;
    }

    public isEqualTo(other:FlowAnchor):boolean
    {
        if( !other )
            return false;

        if( other.port && other.block )
            return other.port.isEqualTo(this.port) &&
                other.block.isEqualTo(this.block) &&
                other.position.isEqual(this.position);
        else if( other.position )
            return other.position.isEqual(this.position);
        else
            return false;
    }

    public clone(ctx:FlowSerdesContext):IFlowAnchor
    {
        return new ActiveWireFlowAnchor(this._mousePosition);
    }
}

export class FlowSerdesContext
{
    private _idToFlowBlock:any = {};

    public putBlock(block:FlowBlock)
    {
        this._idToFlowBlock[block.id] = block;
    }

    public findBlock(flowBlockId:GUID):FlowBlock | null
    {
        const foundBlock = this._idToFlowBlock[flowBlockId];
        return foundBlock ? foundBlock : null;
    }
}

export class FlowPoint
{
    constructor(public readonly x, public readonly y)
    {
    }

    makeCopy():FlowPoint
    {
        return new FlowPoint(this.x, this.y);
    }

    add(point:FlowPoint):FlowPoint
    {
        return new FlowPoint(this.x + point.x, this.y + point.y);
    }

    clampToZero():FlowPoint
    {
        let x = this.x;
        let y = this.y;

        if( x < 0 )
            x = 0;

        if( y < 0 )
            y = 0;

        return new FlowPoint(x, y);
    }

    toCssStyle():any
    {
        let style:any = {};
        style.transform = `translateX(${this.x}px) translateY(${this.y}px)`;

        return style;
    }

    isEqual(other:SurfacePoint):boolean
    {
        return other && other.x === this.x && other.y === this.y;
    }

    toString()
    {
        return `${this.x},${this.y}`;
    }

    public static fromJson(json:FlowPoint):FlowPoint
    {
        return new FlowPoint(json.x, json.y);
    }
}

export class FlowSize
{
    public static readonly Unknown = new FlowSize(NaN, NaN);

    constructor(public readonly width:number,
                public readonly height:number)
    {
    }

    toCssStyle():any
    {
        return {width: this.width, height: this.height};
    }

    isEqual(other:FlowSize):boolean
    {
        return other && other.width === this.width && other.height === this.height;
    }

    makeCopy():FlowSize
    {
        return new FlowSize(this.width, this.height);
    }

    toString()
    {
        return `${this.width},${this.height}`;
    }

    public static fromJson(size:FlowSize):FlowSize
    {
        return new FlowSize(size.width, size.height);
    }
}

export class FlowBounds
{
    @observable
    public position:FlowPoint;

    @observable
    public size:FlowSize;

    constructor(initialPosition:FlowPoint,
                initialSize:FlowSize)
    {
        this.position = initialPosition;
        this.size = initialSize;
    }

    public static from(x:number, y:number, width:number, height:number):FlowBounds
    {
        return new FlowBounds(new FlowPoint(x, y), new FlowSize(width, height));
    }

    public add(otherBounds:FlowBounds):FlowBounds
    {
        const pos0 = this.position;
        const size0 = this.size;
        const pos1 = otherBounds.position;
        const size1 = otherBounds.size;

        const x = pos0.x + pos1.x;
        const y = pos0.y + pos1.y;
        const w = size0.width + size1.width;
        const h = size0.height + size1.height;

        return FlowBounds.from(x, y, w, h);
    }

    toCssStyle():any
    {
        let pos = this.position.toCssStyle();
        let size = this.size.toCssStyle();

        return Object.assign({}, pos, size);
    }

    isEqual(other:FlowBounds):boolean
    {
        return other &&
            this.position.isEqual(other.position) &&
            this.size.isEqual(other.size);
    }

    makeCopy():FlowBounds
    {
        return new FlowBounds(this.position.makeCopy(), this.size.makeCopy());
    }

    clone():FlowBounds
    {
        return new FlowBounds(this.position.makeCopy(), this.size.makeCopy());
    }

    toString()
    {
        return `${this.position}->${this.size}`;
    }

    public static fromJson(bounds:FlowBounds):FlowBounds
    {
        const point = FlowPoint.fromJson(bounds.position);
        const size = FlowSize.fromJson(bounds.size);

        return new FlowBounds(point, size);
    }
}

export class FlowDropContext
{
    constructor(public point:FlowPoint,
                public componentToAdd:FlowComponent)
    {
    }
}

export class FlowPortContext
{
    constructor(public flowBlock:FlowBlock,
                public flowPort:FlowPort,
                public position:FlowPoint)
    {
    }
}

export class FlowBlockContext
{
    constructor(public flowBlock:FlowBlock,
                public defaultInputAnchor:FlowAnchor | null,
                public defaultOutputAnchor:FlowAnchor | null)
    {
    }
}

export class FlowWireMenuContext
{
    constructor(public mouseDownEvent:MouseEvent,
                public wire:FlowWire)
    {
    }
}

export class FlowBlockMenuContext
{
    constructor(public mouseDownEvent:MouseEvent,
                public block:FlowBlock)
    {
    }
}

export enum FlashBlockType
{
    Info = 'Info',
    Warning = 'Warning',
    Error = 'Error'
}

export class FlashBlockRequest
{
    constructor(public readonly flowBlockId:FlowBlockId,
                public readonly type:FlashBlockType)
    {
    }
}

export class FlowArtifact
{
    @observable
    public isVisible:boolean = true;

    @observable
    public sections:FlowSection[] = [];

    constructor(public readonly artifactName:string)
    {
    }
}

export class FlowManifest
{
    @observable
    public artifacts:FlowArtifact[] = [];

    private _idToComponent:Map<CloudComponentId, FlowComponent> = new Map();

    @bind
    public registerComponent(c:FlowComponent):void
    {
        this._idToComponent.set(c.component.id, c);
    }

    @bind
    public unregisterComponent(c:FlowComponent):void
    {
        this._idToComponent.delete(c.component.id);
    }

    @bind
    public findComponent(id:CloudComponentId):FlowComponent | null
    {
        return this._idToComponent.get(id);
    }

    public getAllComponents():FlowComponent[]
    {
        return Array.from(this._idToComponent.values());
    }
}

export class FlowComponentsPanelState
{
    @observable
    filterString:string = null;

    @observable
    manifest:FlowManifest = null;
}

export type SimulationId = GUID;

export class FlowSimulation
{
    public id:SimulationId = IdUtil.newGUID();

    public readonly slot:Slot;

    @observable
    public functionId:number;

    @observable
    public outputComponentId:string;

    @observable
    public execType:FlowExecType = FlowExecType.REST;

    @observable
    public status:SimulationStatus = SimulationStatus.InitialNotRunning;

    @observable
    public stopReason:SimulationStopReason = null;

    @observable
    public messages:SimulationMessage[] = [];

    @observable
    public isEverHadData:boolean = false;

    private constructor(functionId:number, outputComponentId:string, slot:Slot = null)
    {
        this.functionId = functionId;
        this.outputComponentId = outputComponentId;
        this.slot = slot;
    }

    public get isForFlowEditor():boolean
    {
        return this.slot === null;
    }

    public get isForSlot():boolean
    {
        return this.slot !== null;
    }

    public regenerateId():SimulationId
    {
        const newId = IdUtil.newGUID();
        this.id = newId;

        return newId;
    }

    public static forSlot(slot:Slot, functionId:number, outputComponentId:string)
    {
        return new FlowSimulation(functionId, outputComponentId, slot);
    }

    public static forFlowEditor(flow:Flow)
    {
        return new FlowSimulation(flow.id, flow.output.id);
    }
}

export class SimulationMessage
{
    constructor(public readonly message:string,
                public readonly severity:MessageSeverity,
                public readonly source:MessageSource,
                public readonly flowErrorRef:FlowValidationError = null,
                public readonly cloudFlowErrorRef:ValidationErrorDescription = null)
    {
    }

    public get isFlowError():boolean
    {
        return this.flowErrorRef !== null;
    }

    public get isCloudFlowError():boolean
    {
        return this.cloudFlowErrorRef !== null;
    }
}

export enum MessageSeverity
{
    Info = 'info',
    Warn = 'warn',
    Error = 'error',
    Verbose = 'verbose'
}

export enum MessageSource
{
    UI = 'ui',
    Network = 'network',
    Cloud = 'cloud',
    TopologyControl = 'topology-control',
    VisualData = 'visual-data'
}

export enum SimulationStatus
{
    InitialNotRunning = 'InitialNotRunning',
    Starting = 'Starting',
    WaitingForFirstVisualData = 'WaitingForFirstVisualData',
    Running = 'Running',
    Stopping = 'Stopping',
    Stopped = 'Stopped'
}

export enum SimulationStopReason
{
    UserStoppedTopology = 'UserStoppedTopology',
    FlowValidationErrors = 'ValidationErrors',
    CloudFailedToStart = 'CloudFailedToStart',
    CloudTerminated = 'CloudTerminated',
    ErrorOverSSEChannel = 'ErrorOverSSEChannel',
}

export enum FlowValidationErrorType
{
    Port = 'Port',
    Property = 'Property',
}

export class FlowValidationError
{
    constructor(public type:FlowValidationErrorType,
                public messages:string[],
                public flowBlockId:string,
                public flowBlockName:string,
                public flowPortId?:string,
                public flowPropertyName?:string,
                public nested?:FlowValidationError[])
    {
    }

    public static portError(messages:string[],
                            flowBlockId:string,
                            flowBlockName:string,
                            flowPortId:string)
    {
        return new FlowValidationError(
            FlowValidationErrorType.Port,
            messages,
            flowBlockId,
            flowBlockName,
            flowPortId
        );
    }

    public static propertyError(messages:string[],
                                flowBlockId:string,
                                flowBlockName:string,
                                flowPropertyName:string,
                                nested:FlowValidationError[] = [])
    {
        return new FlowValidationError(
            FlowValidationErrorType.Property,
            messages,
            flowBlockId,
            flowBlockName,
            null,
            flowPropertyName,
            nested
        );
    }
}

export class VisualLink
{
    private constructor(public readonly isForDebugBlock:boolean,
                        public readonly boxId:string)
    {
    }

    public clone():VisualLink
    {
        return new VisualLink(this.isForDebugBlock, this.boxId);
    }

    public static forDebugBlock():VisualLink
    {
        return new VisualLink(true, null);
    }

    public static forBox(boxId:BoxId):VisualLink
    {
        return new VisualLink(false, boxId);
    }

    public static fromJson(json:VisualLink):VisualLink
    {
        const isForDebugBlock = json.isForDebugBlock;
        const boxId = json.boxId;

        return new VisualLink(isForDebugBlock, boxId);
    }
}
